import { Helmet } from "react-helmet-async";
import { isStandalone } from "../lib/pwa";

const SITE_URL =
  process.env.REACT_APP_SITE_URL || "https://terminalvalue.io";
const SITE_NAME = "TerminalValue.io";
const DEFAULT_DESC =
  "A manually-reviewed index of investing tools.";
// Bumped when OG image design changes. Must match OG_VERSION in
// backend/server.py and frontend/scripts/prerender/routes.js so CDN
// cache for the previous design gets invalidated on the next deploy.
export const OG_VERSION = "4";
const DEFAULT_OG_IMAGE = `${SITE_URL}/api/og/home.png?v=${OG_VERSION}`;

export const siteUrl = (path = "") => {
  if (!path || path === "/") return SITE_URL + "/";
  return SITE_URL + (path.startsWith("/") ? path : "/" + path);
};

/**
 * Drop-in <Seo /> component. Renders <head> tags via react-helmet-async.
 * Safe to use on any page; pass `noindex` for admin/private routes.
 *
 *   <Seo title="..." description="..." path="/tool/foo" image="..." jsonLd={{...}} />
 */
export default function Seo({
  title,
  description = DEFAULT_DESC,
  path,
  image,
  noindex = false,
  jsonLd,
  ogType = "website",
  bare = false, // tool pages: Google appends the site name itself; keep the hook un-truncated
}) {
  // In the installed PWA the window chrome already shows the app name (manifest
  // `name`), so prefixing the brand again here produced a doubled title bar
  // ("TerminalValue.io … · TerminalValue.io …"). When standalone, use a
  // brand-free document title; browsers/tabs (and crawlers, via prerender where
  // standalone is always false) keep the SEO-friendly brand-suffixed title.
  const standalone = isStandalone();
  const fullTitle = title
    ? standalone || bare
      ? title
      : `${title} — ${SITE_NAME}`
    : standalone
      ? "The investing tools index"
      : `${SITE_NAME} — The investing tools index`;
  const url = path ? siteUrl(path) : SITE_URL + "/";
  const ogImage = image || DEFAULT_OG_IMAGE;
  const ld = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-image-preview:large" />
      )}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {ld.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
